import { loginManager } from "@/services/security/login-manager";
import { userManager } from "@/services/security/user-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import * as Sentry from "@sentry/vue";
import router from "@/router";
import { formStore, mainStore } from "@/main";
import { useMainStore } from "@/store/main/main";
import { apiConnection } from "@/services/api-connection";
import { extractMutationVerdict, extractValidationErrors } from "@/services/form/validation-errors";
import { usePlatformStore } from "@/store/platform/platform";
import { Capacitor } from "@capacitor/core";

// Messages JWT déclenchant un refresh transparent du token.
const JWT_ERROR_MESSAGES = ["Expired JWT Token", "Invalid JWT Token", "JWT Token not found"];

// 404 levé par CurrentPlatformListener (back) quand l'hôte appelé n'est plus une plateforme.
const REMOVED_PLATFORM_MESSAGE = /^Platform \S+ not configured$/;

// Gestion centralisée des erreurs HTTP. Appelée par apiConnection.fetchApi via
// errorManager.dispatchError ; regroupe le routage par statut et les handlers
// spécifiques (auth, forbidden, maintenance, not found, server, generic).
//
// Option `ctx.silent` (apiConnection.post) : aucune alerte 403 ni toast d'erreur, l'erreur est
// rejetée normalement (`handled: true`) pour que l'appelant l'affiche à sa manière.
//
// Contrat du payload rejeté : { ...data, handled, [status] }. `handled: true` indique que
// l'erreur a déjà été notifiée/routée par ce service (les appelants ne doivent pas la
// re-notifier). `status` n'est posé que par les handlers qui le normalisent explicitement
// (maintenance 503, interdit 403, rate limiting 429, introuvable 404, statuts inattendus via
// handleUnexpectedStatus) — pour les autres statuts routés (400/401/409/422/428/500), le corps
// rejeté ne contient que ce que le back a renvoyé, sans `status` normalisé. C'est pourquoi login-manager._isInvalidCredentials
// discrimine sur `code`, écho numérique du statut posé par Lexik (`{code: 401, message}`),
// et non sur `status`. `data` peut aussi exposer son propre champ `code` métier ; les
// appelants qui distinguent une erreur HTTP d'une erreur métier doivent donc lire `status`
// en priorité quand il est présent (cf. login-manager._performRefresh).
class ErrorManager {
  constructor() {
    this.removedApiPath = "";
    // Table de routage statut HTTP → handler. Tous les handlers routés partagent
    // la signature (error, { resolve, reject, ctx }). Statut absent → handleUnexpectedStatus.
    this.statusHandlers = {
      400: this.handleError,
      401: this.handleAuthenticationError,
      403: this.handleForbiddenError,
      409: this.handleError,
      422: this.handleError,
      428: this.handleError,
      503: this.handleMaintenanceError,
      404: this.handleNotFoundError,
      500: this.handleServerError,
      429: this.handleTooManyRequests,
    };
  }

  // Clôt le suivi de mutation ouvert par apiConnection.fetchApi en publiant les validationErrors
  // Symfony quand le back les renvoie en 400/422/428. ModalFormComponent attend ce verdict pour
  // décider de se fermer ou de rester ouverte avec les erreurs. Doit être appelé sur chaque chemin
  // terminal, y compris ceux qui ne rejettent pas : sinon la modale reste bloquée jusqu'au délai max.
  // `reason` qualifie les échecs sans réponse du back (réseau, annulation) : aucun toast n'est
  // affiché sur ces chemins, c'est la modale qui doit prévenir l'utilisateur.
  endMutation(ctx, error = null, reason = null) {
    formStore.endMutation(ctx?.mutationHandle, {
      ok: false,
      validationErrors: extractValidationErrors(error?.response?.data),
      ...(reason ? { reason } : {}),
    });
  }

  dispatchError(error, resolve, reject, ctx) {
    // Réseau hors-ligne ou appels annulés : pas de reject (comportement conservé).
    if (error.code === "ERR_NETWORK") {
      mainStore.setInternet(false);
      this.endMutation(ctx, null, "network");
      return;
    }
    if (["ECONNABORTED", "ERR_CANCELED"].includes(error.code)) {
      this.endMutation(ctx, null, "canceled");
      return;
    }
    mainStore.setInternet(true);

    // Statut HTTP normalisé : axios expose error.response.status ; certaines
    // erreurs métier placent le code dans le body (data.status / data.code).
    const status = this.getStatus(error);

    // Le 401 est exclu ici : son issue est ambiguë (refresh JWT + rejeu de l'appel), c'est
    // handleAuthenticationError qui clôt la mutation sur ses seules branches terminales.
    if (status !== 401) {
      this.endMutation(ctx, error);
    }

    const handler = this.statusHandlers[status] ?? this.handleUnexpectedStatus;
    handler.call(this, error, { resolve, reject, ctx });
  }

  getStatus(error) {
    return error.status ?? error.response?.status ?? error.response?.data?.status ?? error.response?.data?.code;
  }

  // Statut inattendu : capture Sentry (observabilité) + toast générique. Le statut HTTP est
  // normalisé dans le rejet pour que les appelants distinguent un 502/504 de passerelle.
  handleUnexpectedStatus(error, { reject, ctx }) {
    Sentry.captureException(error);
    const status = this.getStatus(error);
    this.handleError(error, { reject: reject && ((payload) => reject({ ...payload, status })), ctx });
  }

  handleAuthenticationError(error, { resolve, reject, ctx }) {
    const data = error.response?.data ?? null;
    const detail = data?.detail;
    const message = data?.message;

    if (JWT_ERROR_MESSAGES.includes(message)) {
      if (ctx?.isJwtRetry) {
        userManager.logout(true);
        this.endMutation(ctx);
        reject?.({ handled: true });
        return;
      }

      // Branche non terminale : l'appel va être rejoué, c'est refreshTokenAndRetry qui publie le
      // verdict du rejeu sur la mutation d'origine. Ne rien publier ici, sinon la modale se
      // fermerait avant le vrai verdict.
      //
      // Fire-and-forget : la promesse d'origine est résolue/rejetée via les callbacks
      // `resolve`/`reject`, pas via la promesse retournée (le try/catch interne ne rejette
      // jamais).
      void this.refreshTokenAndRetry(resolve, reject, ctx);
      return;
    }

    if (detail === "Unauthorized") {
      this.endMutation(ctx);
      this.handleUnauthorizedSession(data);
      return;
    }

    if (detail === "Magic link is expired") {
      this.endMutation(ctx);
      // Fire-and-forget : la promesse d'origine est résolue via le callback `resolve`,
      // pas via la promesse retournée (le try/catch interne ne rejette jamais).
      void this.expiredMagicLinkAlert();
      return;
    }

    this.endMutation(ctx, error);

    if (data) {
      reject({ ...data, handled: true });
      return;
    }

    router.push("/login");
  }

  // JWT expiré/invalide : refresh transparent puis rejeu de l'appel initial.
  // Échec du refresh OU du rejeu → déconnexion avec referer.
  //
  // La promesse d'origine (celle renvoyée par apiConnection.fetchApi à l'appelant initial) doit
  // être réglée sur chaque branche : un simple `return` après logout() la laisserait pending
  // indéfiniment (fuite de promesse, appelant potentiellement bloqué en attente de cette
  // requête).
  async refreshTokenAndRetry(resolve, reject, ctx) {
    // refreshConnection() ne rejette jamais (contrat : resolve({success:true}) ou
    // resolve(false)) — un échec de refresh doit donc être détecté sur la valeur
    // retournée, pas via un catch qui ne se déclenchera plus.
    const refreshResult = await loginManager.refreshConnection();
    if (!refreshResult || !refreshResult.success) {
      userManager.logout(true);
      this.endMutation(ctx);
      reject({ handled: true });
      return;
    }

    try {
      const response = await apiConnection.fetchApi(
        ctx.method,
        ctx.path,
        ctx.params,
        ctx.security,
        ctx.platformFetch,
        ctx.cancelable,
        true,
        null,
        ctx.silent,
      );
      router.push({
        name: router.currentRoute.value.name,
        params: router.currentRoute.value.params,
        replace: true,
      });

      // ModalFormComponent attend la mutation d'origine, pas celle ouverte par le rejeu.
      formStore.endMutation(ctx?.mutationHandle, extractMutationVerdict(response));
      resolve(response);
    } catch (error) {
      userManager.logout(true);
      this.endMutation(ctx);
      reject({ ...(error ?? {}), handled: true });
    }
  }

  // 401 « Unauthorized » : magic link présent → alerte de renvoi, sinon déconnexion.
  handleUnauthorizedSession(data) {
    Sentry.captureException(data);
    if (apiConnection.getMagicLinkToken()) {
      // Fire-and-forget : la promesse d'origine est résolue via le callback `resolve`,
      // pas via la promesse retournée (le try/catch interne ne rejette jamais).
      void this.expiredMagicLinkAlert();
    } else {
      userManager.logout(true);
    }
  }

  handleForbiddenError(error, { reject, ctx }) {
    if (!ctx?.silent) {
      this.handleUnauthorizedError(error);
    }
    reject({ ...(error.response?.data ?? {}), status: 403, handled: true });
  }

  // Maintenance : router vers la page dédiée ET régler la promesse d'origine. Sans ce
  // reject, tout appel reçu en 503 restait pendant à vie — les stores dépendants
  // (userStore.fetchUserInfos notamment) ne se remplissaient jamais, et les vues remontées
  // jugeaient les droits sur un user vide (alerte « Accès interdit » sur l'écran de
  // maintenance, cf. ST143-TI-17527).
  handleMaintenanceError(error, { reject }) {
    useMainStore().setMaintenance(true);
    router.push({ name: "Maintenance" });
    // Le corps renvoyé par le back en maintenance est une chaîne, pas un objet : on ne le
    // déstructure que s'il s'agit bien d'un objet.
    const data = error?.response?.data;
    reject?.({ ...(data && "object" === typeof data ? data : {}), status: 503, handled: true });
  }

  // Ressource introuvable : capture Sentry conservée (observabilité) + message dédié.
  handleNotFoundError(error, { reject, ctx }) {
    if (this.handleRemovedPlatform(error, { reject, ctx })) {
      return;
    }
    Sentry.captureException(error);
    if (!ctx?.silent) {
      notificationManager.showNotification("error", i18n.global.t("error.not_found"));
    }
    reject?.({ ...(error.response?.data ?? {}), status: 404, handled: true });
  }

  // Rate limiting : cas nominal de sécurité, pas une anomalie — ni capture Sentry ni toast
  // générique, la vue appelante affiche déjà le message. Le back renvoie `{error}` et non
  // `{message}` (ThrottledAuthenticationFailureHandler) : on normalise pour les appelants.
  handleTooManyRequests(error, { reject }) {
    const data = error.response?.data ?? {};
    reject?.({
      ...data,
      message: this.resolveDisplayMessage(data.message, data.error),
      status: 429,
      handled: true,
    });
  }

  handleServerError(error, { reject, ctx }) {
    const data = error.response?.data ?? null;
    const message = data?.message ?? "";

    if (this.handlePlatformNotFound(message)) {
      return;
    }
    if (this.handleUndecryptableToken(data)) {
      return;
    }
    Sentry.captureException(error);
    this.handleError(error, { reject, ctx });
  }

  // App mobile : la plateforme mémorisée a été supprimée. Sans oublier plateforme et session,
  // chaque lancement rejoue les mêmes 404 et l'utilisateur ne peut plus revenir au choix de
  // plateforme. Sur le web, la plateforme est résolue par le domaine front, pas mémorisée.
  // Limité à une session existante ou au démarrage silencieux du login : pendant le choix de
  // plateforme (MobileLoginView), l'utilisateur doit voir l'erreur.
  handleRemovedPlatform(error, { reject, ctx }) {
    const message = error?.response?.data?.message ?? "";
    if (Capacitor.getPlatform() === "web" || !REMOVED_PLATFORM_MESSAGE.test(message)) {
      return false;
    }
    // Appels parallèles : le premier 404 a déjà oublié la plateforme (et la session), les
    // suivants, partis avant, doivent être rejetés avec le même silence.
    const alreadyForgotten =
      !loginManager.apiPath && this.removedApiPath !== "" && String(ctx?.path ?? "").startsWith(this.removedApiPath);
    if (!alreadyForgotten) {
      if (!(loginManager.isToken() || ctx?.silent)) {
        return false;
      }
      Sentry.captureMessage(message, "info");
      this.removedApiPath = loginManager.apiPath || loginManager.getApiPath() || "";
      loginManager.clearApiPath();
      usePlatformStore().init();
      if (loginManager.isToken()) {
        userManager.logout();
      }
    }
    reject?.({ ...(error.response?.data ?? {}), status: 404, handled: true, platformRemoved: true });
    return true;
  }

  // Plateforme introuvable : 500 spécifique renvoyant vers la page dédiée (pas de reject).
  handlePlatformNotFound(message) {
    if (!message.includes("Entity of type 'App\\Entity\\Core\\Platform' for IDs frontHostname")) {
      return false;
    }
    useMainStore().setMaintenance(true);
    router.push({ name: "PlatformNotFound" });
    return true;
  }

  // Token indéchiffrable : déconnexion.
  handleUndecryptableToken(data) {
    if (data?.detail !== "Unable to load and decrypt the token.") {
      return false;
    }
    userManager.logout(true);
    return true;
  }

  // Magic link expiré : propose le renvoi d'un nouveau lien puis déconnecte.
  async expiredMagicLinkAlert() {
    const alert = await notificationManager.showAlert(
      "info",
      i18n.global.t("magic_link_expired"),
      i18n.global.t("magic_link_expired_subtitle"),
      true,
      true,
      i18n.global.t("send_magic_link"),
      i18n.global.t("reconnect"),
      "",
      "magicLink",
    );

    if (alert.isConfirmed) {
      const response = await apiConnection.post("/app/magic-link/resend");
      if (response && response.success) {
        notificationManager.showNotification("success", i18n.global.t("magic_link_sent"));
        userManager.logout();
      } else {
        notificationManager.showNotification("error", i18n.global.t("magic_link_sent"));
      }
    } else {
      userManager.logout();
    }
  }

  // Alerte bloquante « accès non autorisé » (réutilisée par 401 Unauthorized et 403).
  handleUnauthorizedError() {
    notificationManager.showAlert(
      "error",
      i18n.global.t("error.message.not_authorized_title"),
      i18n.global.t("error.message.not_authorized"),
    );
  }

  // Toast d'erreur générique. `reject` est optionnel : les call sites legacy qui
  // appellent handleError(error) sans second argument continuent de fonctionner.
  // `??` ne suffit pas : le back renvoie parfois `message: ""` (ou `{success: false,
  // hasError: true, message: ""}`), une chaîne définie mais vide, qui passait le fallback et
  // produisait un toast sans texte. Toute valeur non exploitable retombe sur le message générique.
  resolveDisplayMessage(...candidates) {
    const message = candidates.find((candidate) => typeof candidate === "string" && "" !== candidate.trim());
    return message ? message.trim() : i18n.global.t("error.error_occured");
  }

  handleError(error, { reject, ctx } = {}) {
    if (error?.handled) {
      return;
    }
    if (!ctx?.silent) {
      const message = this.resolveDisplayMessage(error?.response?.data?.message);
      notificationManager.showNotification("error", message);
    }
    reject?.({ ...(error.response?.data ?? { message: error?.message }), handled: true });
  }
}

export const errorManager = new ErrorManager();
